import React, { useState, useEffect } from "react";
import {
  X,
  Search,
  Filter,
  Target,
  Users,
  DollarSign,
  Settings,
  Eye,
  CheckCircle,
  Calendar,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  RefreshCw,
  Gauge,
  Presentation,
} from "lucide-react";
import { useStrategicMap } from "../hooks/useStrategicMap";
import { authenticatedFetch } from '../services/auth';
import { API_BASE } from '../utils/apiConfig';
import { generateStrategicMapPPTX } from "./pptxMapExport";

const StrategicMap = ({ closeStrategicMap }) => {
  const { strategicMapData, loading, error, dataSource } = useStrategicMap();
  const [searchTerm, setSearchTerm] = useState("");

  const [selectedPerspectives, setSelectedPerspectives] = useState([
    "Financeiro",
    "Clientes",
    "Processos",
    "Pessoas",
  ]);
  const [selectedPeriod, setSelectedPeriod] = useState("");

  // >>> NOVO: controle global de visibilidade dos indicadores
  // null = não forçar; true = mostrar todos; false = ocultar todos
  const [globalIndicatorsState, setGlobalIndicatorsState] = useState(null);
  // "Versor" para disparar novo comando mesmo que o valor seja o mesmo
  const [globalIndicatorsVersion, setGlobalIndicatorsVersion] = useState(0);

  const applyGlobalIndicators = (value) => {
    setGlobalIndicatorsState(value);
    setGlobalIndicatorsVersion((v) => v + 1);
  };
  // <<< NOVO

  const [isGeneratingPptx, setIsGeneratingPptx] = useState(false);
  const handleMapPptxExport = async () => {
    if (!filteredData || filteredData.length === 0) {
      alert("Nenhum dado disponível para exportação. Ajuste os filtros ou o período.");
      return;
    }
    try {
      setIsGeneratingPptx(true);
      await generateStrategicMapPPTX({ strategicMapData: filteredData, selectedPeriod, globalPerf });
    } catch (e) {
      console.error("Erro ao gerar PPTX do Mapa:", e);
      alert("❌ Erro ao gerar o PPTX. Verifique o console para mais detalhes.");
    } finally {
      setIsGeneratingPptx(false);
    }
  };

  // Extrair períodos únicos dos dados e definir penúltimo período
  const availablePeriods = [...new Set(strategicMapData.map(p => p.period))].filter(Boolean);

  // Definir o penúltimo período ao carregar os dados
  useEffect(() => {
    if (availablePeriods.length > 0 && !selectedPeriod) {
      const sortedPeriods = availablePeriods.sort((a, b) => a.localeCompare(b));
      const penultimatePeriod = sortedPeriods.length > 1 ? sortedPeriods[sortedPeriods.length - 2] : sortedPeriods[0];
      setSelectedPeriod(penultimatePeriod);
    }
  }, [availablePeriods, selectedPeriod]);

  // Performance global ponderada do período selecionado (YYYY-MM)
  const [globalPerf, setGlobalPerf] = useState(null);
  useEffect(() => {
    if (!selectedPeriod || !/^\d{4}-\d{2}$/.test(selectedPeriod)) {
      setGlobalPerf(null);
      return;
    }
    const [yy, mm] = selectedPeriod.split("-").map(Number);
    const lastDay = new Date(yy, mm, 0).getDate();
    const endDate = `${yy}-${String(mm).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    const prev = new Date(yy, mm - 2, 1);
    const startDate = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}-01`;

    let cancelled = false;
    (async () => {
      try {
        const resp = await authenticatedFetch(
          `${API_BASE}/api/bsc/performance?start_date=${startDate}&end_date=${endDate}`,
        );
        if (!cancelled && resp.ok) {
          setGlobalPerf(await resp.json());
        } else if (!cancelled) {
          setGlobalPerf(null);
        }
      } catch (e) {
        if (!cancelled) setGlobalPerf(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedPeriod]);

  // Cores por faixa de performance
  const perfText = (v) =>
    v >= 100 ? "text-green-600" : v >= 80 ? "text-emerald-600" : v >= 50 ? "text-amber-600" : "text-red-600";
  const perfBg = (v) =>
    v >= 100 ? "bg-green-500" : v >= 80 ? "bg-emerald-500" : v >= 50 ? "bg-amber-500" : "bg-red-500";

  const getPerformanceColor = (percentAchieved) => {
    if (percentAchieved >= 100) return "text-green-600 bg-green-100";
    if (percentAchieved > 95) return "text-blue-600 bg-blue-100";
    if (percentAchieved < 95) return "text-yellow-600 bg-yellow-100";
    return "text-red-600 bg-red-100";
  };

  const getPerformanceTextColor = (percentAchieved) => {
    if (percentAchieved >= 100) return "text-green-600";
    if (percentAchieved > 95) return "text-blue-600";
    if (percentAchieved < 95) return "text-yellow-600";
    return "text-red-600";
  };

  const getPerformanceIcon = (percentAchieved) => {
    if (percentAchieved >= 100) return <TrendingUp className="w-4 h-4" />;
    if (percentAchieved > 95) return <TrendingUp className="w-4 h-4" />;
    if (percentAchieved < 95) return <AlertCircle className="w-4 h-4" />;
    return <TrendingDown className="w-4 h-4" />;
  };

  const getPerspectiveIcon = (perspective) => {
    switch (perspective) {
      case "Financeiro":
        return <DollarSign className="w-5 h-5" />;
      case "Clientes":
        return <Users className="w-5 h-5" />;
      case "Processos":
        return <Settings className="w-5 h-5" />;
      case "Pessoas":
        return <Target className="w-5 h-5" />;
      default:
        return <Target className="w-5 h-5" />;
    }
  };

  const getPerspectiveColor = (perspective) => {
    switch (perspective) {
      case "Financeiro":
        return "bg-emerald-600";
      case "Clientes":
        return "bg-blue-600";
      case "Processos":
        return "bg-purple-600";
      case "Pessoas":
        return "bg-orange-600";
      default:
        return "bg-gray-600";
    }
  };

  const getObjectiveCardColor = (perspective, average) => {
    const baseColors = {
      "Financeiro": "from-emerald-50 to-emerald-100 border-emerald-200 hover:border-emerald-300",
      "Clientes": "from-blue-50 to-blue-100 border-blue-200 hover:border-blue-300",
      "Processos": "from-purple-50 to-purple-100 border-purple-200 hover:border-purple-300",
      "Pessoas": "from-orange-50 to-orange-100 border-orange-200 hover:border-orange-300",
    };

    const intensityClass = average >= 90 ? "shadow-lg ring-2 ring-green-200" :
      average >= 70 ? "shadow-md" : "shadow-sm";

    return `bg-gradient-to-br ${baseColors[perspective] || baseColors["Financeiro"]} ${intensityClass}`;
  };

  const togglePerspective = (perspective) => {
    setSelectedPerspectives((prev) =>
      prev.includes(perspective)
        ? prev.filter((p) => p !== perspective)
        : [...prev, perspective],
    );
  };

  const filteredData = strategicMapData.filter((perspectiveData) => {
    const matchesPerspective = selectedPerspectives.includes(
      perspectiveData.perspective,
    );

    const matchesPeriod = perspectiveData.period === selectedPeriod;

    if (!searchTerm.trim()) return matchesPerspective && matchesPeriod;

    const term = searchTerm.toLowerCase();
    const matchesSearch = perspectiveData.objectives.some(
      (objective) =>
        objective.objective.toLowerCase().includes(term) ||
        objective.indicators.some((indicator) =>
          indicator.indicator.toLowerCase().includes(term),
        ),
    );

    return matchesPerspective && matchesPeriod && matchesSearch;
  });

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-50 z-40 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando Mapa Estratégico...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-gray-50 z-40 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
          <button
            onClick={closeStrategicMap}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Fechar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header do Mapa Estratégico */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">

            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Target className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Mapa Estratégico BSC</h1>
                <p className="text-gray-600 text-sm">Visão Integrada dos Objetivos Estratégicos</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      <div className="max-w-8xl mx-auto p-6">
        {/* Filtros */}
        <div className="bg-white rounded-xl p-5 shadow-lg mb-5">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filtros e Busca
            </h3>

            {/* >>> NOVO: botões de ação global */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => applyGlobalIndicators(true)}
                className="px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
                title="Mostrar todos os indicadores"
              >
                Mostrar todos
              </button>
              <button
                onClick={() => applyGlobalIndicators(false)}
                className="px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
                title="Ocultar todos os indicadores"
              >
                Ocultar todos
              </button>
              <button
                onClick={handleMapPptxExport}
                disabled={isGeneratingPptx}
                className="flex items-center gap-2 px-4 py-1.5 rounded-md bg-gradient-to-r from-[#7a2433] to-[#5e1a26] hover:from-[#8a2a3b] hover:to-[#6e1f2d] text-white text-sm font-medium shadow transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                title="Exportar o Mapa Estratégico em PPTX"
              >
                {isGeneratingPptx ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Gerando PPTX...
                  </>
                ) : (
                  <>
                    <Presentation className="w-4 h-4" />
                    Exportar PPTX
                  </>
                )}
              </button>
            </div>
            {/* <<< NOVO */}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Busca */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buscar Objetivos/Indicadores
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Digite para buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>

            {/* Filtro de Período */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Período
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 appearance-none bg-white"
                >
                  {availablePeriods.sort((a, b) => a.localeCompare(b)).map((period) => (
                    <option key={period} value={period}>
                      {period}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Filtros de Perspectiva */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Perspectivas
              </label>
              <div className="flex flex-wrap gap-2">
                {["Financeiro", "Clientes", "Processos", "Pessoas"].map(
                  (perspective) => (
                    <button
                      key={perspective}
                      onClick={() => togglePerspective(perspective)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all border-2 ${selectedPerspectives.includes(perspective)
                        ? `${getPerspectiveColor(perspective)} text-white border-transparent`
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                        }`}
                    >
                      {getPerspectiveIcon(perspective)}
                      {perspective}
                      {selectedPerspectives.includes(perspective) && (
                        <CheckCircle className="w-3 h-3" />
                      )}
                    </button>
                  ),
                )}
              </div>
            </div>

          </div>
        </div>

        {/* Cabeçalho principal do diagrama com a Performance Global do período */}
        {globalPerf && typeof globalPerf.performance === "number" && (
          <div className="bg-white rounded-xl shadow-lg mb-6 p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl shadow-lg ${perfBg(globalPerf.performance)}`}>
                  <Gauge className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Mapa Estratégico</h3>
                  <div className="flex items-end gap-3 mt-1 flex-wrap">
                    <span className={`text-4xl font-bold leading-none ${perfText(globalPerf.performance)}`}>
                      {globalPerf.performance.toFixed(1)}%
                    </span>
                    <span className="text-sm text-gray-500 mb-1">
                      Performance global{globalPerf.periodLabel ? ` — ${globalPerf.periodLabel}` : ""}
                    </span>
                    {globalPerf.previous != null && (
                      <span
                        className={`text-sm font-medium flex items-center gap-1 mb-1 ${
                          globalPerf.trend === "up"
                            ? "text-green-600"
                            : globalPerf.trend === "down"
                              ? "text-red-600"
                              : "text-gray-500"
                        }`}
                      >
                        {globalPerf.trend === "up" ? (
                          <TrendingUp className="w-4 h-4" />
                        ) : globalPerf.trend === "down" ? (
                          <TrendingDown className="w-4 h-4" />
                        ) : null}
                        {(globalPerf.performance - globalPerf.previous).toFixed(1)} p.p.
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {Array.isArray(globalPerf.perspectives) && globalPerf.perspectives.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {globalPerf.perspectives.map((p) => (
                    <div
                      key={p.perspective}
                      className="px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 min-w-[110px]"
                    >
                      <p className="text-xs text-gray-500 truncate">{p.perspective}</p>
                      <p className={`text-sm font-semibold ${perfText(p.performance)}`}>
                        {p.performance.toFixed(1)}%
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mapa Estratégico */}
        <div className="space-y-8">
          {filteredData.map((perspectiveData) => (
            <PerspectiveLane
              key={`${perspectiveData.perspective}-${perspectiveData.period}`}
              perspectiveData={perspectiveData}
              getPerspectiveIcon={getPerspectiveIcon}
              getPerspectiveColor={getPerspectiveColor}
              getObjectiveCardColor={getObjectiveCardColor}
              getPerformanceColor={getPerformanceColor}
              getPerformanceIcon={getPerformanceIcon}
              getPerformanceTextColor={getPerformanceTextColor}
              searchTerm={searchTerm}
              // >>> NOVO: repassa controle global
              globalIndicatorsState={globalIndicatorsState}
              globalIndicatorsVersion={globalIndicatorsVersion}
            />
          ))}
        </div>

        {strategicMapData.length === 0 && !loading && !error && (
          <div className="bg-white rounded-xl p-12 text-center shadow-lg">
            <Target className="w-16 h-16 text-blue-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-3">
              Nenhum dado disponível
            </h3>
            <p className="text-gray-500 mb-4">
              {dataSource === 'none' ?
                'Não foi possível carregar os dados do mapa estratégico.' :
                'Nenhum objetivo estratégico foi encontrado para exibir.'
              }
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Recarregar Página
            </button>
          </div>
        )}

        {filteredData.length === 0 && strategicMapData.length > 0 && (
          <div className="bg-white rounded-xl p-12 text-center shadow-lg">
            <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-3">
              Nenhuma perspectiva encontrada
            </h3>
            <p className="text-gray-500">
              Ajuste os filtros ou termos de busca para visualizar o mapa
              estratégico.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const PerspectiveLane = ({
  perspectiveData,
  getPerspectiveIcon,
  getPerspectiveColor,
  getObjectiveCardColor,
  getPerformanceColor,
  getPerformanceIcon,
  getPerformanceTextColor,
  searchTerm,
  // >>> NOVO: props globais
  globalIndicatorsState,
  globalIndicatorsVersion,
}) => {
  const { perspective, objectives, average, period } = perspectiveData;

  // Filtrar objetivos que correspondem à busca
  const filteredObjectives = objectives.filter((objective) => {
    if (!searchTerm.trim()) return true;

    const term = searchTerm.toLowerCase();
    return (
      objective.objective.toLowerCase().includes(term) ||
      objective.indicators.some((indicator) =>
        indicator.indicator.toLowerCase().includes(term),
      )
    );
  });

  if (filteredObjectives.length === 0) return null;

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Cabeçalho da Perspectiva */}
      <div className={`${getPerspectiveColor(perspective)} text-white p-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getPerspectiveIcon(perspective)}
            <h2 className="text-xl font-bold">{perspective}</h2>
            <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
              {filteredObjectives.length} objetivo(s)
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-sm opacity-75">Performance Média</div>
              <div className={`text-2xl font-bold px-3 py-1 rounded-lg bg-white/90 ${getPerformanceTextColor(average)}`}>
                {average}%
              </div>
            </div>
            {period && (
              <div className="text-right">
                <div className="text-sm opacity-75">Período</div>
                <div className="text-lg font-semibold">{period}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Grid de Objetivos */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredObjectives.map((objective) => (
            <ObjectiveCard
              key={objective.objectiveId}
              objective={objective}
              perspective={perspective}
              getObjectiveCardColor={getObjectiveCardColor}
              getPerformanceColor={getPerformanceColor}
              getPerformanceIcon={getPerformanceIcon}
              searchTerm={searchTerm}
              // >>> NOVO: repassa controle global
              globalIndicatorsState={globalIndicatorsState}
              globalIndicatorsVersion={globalIndicatorsVersion}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const ObjectiveCard = ({
  objective,
  perspective,
  getObjectiveCardColor,
  getPerformanceColor,
  getPerformanceIcon,
  searchTerm,
  // >>> NOVO: props globais
  globalIndicatorsState,
  globalIndicatorsVersion,
}) => {
  const [showIndicators, setShowIndicators] = React.useState(true);

  // >>> NOVO: aplica o comando global SEM perder o toggle local depois
  React.useEffect(() => {
    if (globalIndicatorsState !== null) {
      setShowIndicators(globalIndicatorsState);
    }
  }, [globalIndicatorsState, globalIndicatorsVersion]);
  // <<< NOVO

  const highlightText = (text, term) => {
    if (!term.trim()) return text;

    const regex = new RegExp(`(${term})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 px-1 rounded">
          {part}
        </mark>
      ) : (
        part
      ),
    );
  };

  const formatValue = (value, unit) => {
    if (unit === "R$") {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value);
    }
    return `${value} ${unit}`;
  };

  return (
    <div
      className={`${getObjectiveCardColor(perspective, objective.average)} border-2 rounded-xl p-5 transition-all duration-200 hover:shadow-lg transform hover:-translate-y-1`}
    >
      {/* Cabeçalho do Objetivo */}
      <div className="mb-4">
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-bold text-gray-800 text-lg flex-1">
            {highlightText(objective.objective, searchTerm)}
          </h3>
          <div className="ml-2 text-right">
            <div className="text-xs text-gray-500">Performance</div>
            <div className={`text-lg font-bold px-2 py-1 rounded ${getPerformanceColor(objective.average)}`}>
              {objective.average}%
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Eye className="w-4 h-4" />
          <span>{objective.indicators.length} indicador(es)</span>
        </div>
        <button
          onClick={() => setShowIndicators((v) => !v)}
          className="text-xs px-2 py-1 rounded-md border border-gray-300 hover:bg-gray-50"
          aria-expanded={showIndicators}
          aria-controls={`indicators-${objective.objectiveId}`}
          title={showIndicators ? "Ocultar indicadores" : "Mostrar indicadores"}
        >
          {showIndicators ? "Ocultar" : "Mostrar"}
        </button>
      </div>

      {/* Lista de Indicadores */}
      <div
        id={`indicators-${objective.objectiveId}`}
        className={`transition-all duration-300 ease-in-out ${showIndicators ? "opacity-100 max-h-[1200px]" : "opacity-0 max-h-0"
          } overflow-hidden`}
      >
        <div className="space-y-3"  >
          <h4 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">
            Indicadores:
          </h4>
          <ul className="space-y-3">
            {objective.indicators.map((indicator) => (
              <li
                key={indicator.indicatorId}
                className="bg-white/70 rounded-lg p-3 border border-gray-100"
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="font-medium text-gray-800 flex-1">
                    {highlightText(indicator.indicator, searchTerm)}
                  </span>
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getPerformanceColor(indicator.percentAchieved)}`}>
                    {getPerformanceIcon(indicator.percentAchieved)}
                    {indicator.percentAchieved.toFixed(1)}%
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <div>
                    <span className="font-medium">Meta:</span> {formatValue(indicator.target, indicator.unit)}
                  </div>
                  <div>
                    <span className="font-medium">Real:</span> {formatValue(indicator.actual, indicator.unit)}
                  </div>
                </div>
                {/* Barra de progresso */}
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${indicator.percentAchieved >= 100 ? 'bg-green-500' :
                        indicator.percentAchieved >= 90 ? 'bg-blue-500' :
                          indicator.percentAchieved >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                      style={{ width: `${Math.min(indicator.percentAchieved, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {/* Footer do Card */}
      <div className="mt-4 pt-3 border-t border-gray-200">
        <div className="flex justify-between items-center text-xs text-gray-500">
          <span>ID: {objective.objectiveId}</span>
          <span className="bg-gray-200 px-2 py-1 rounded">{perspective}</span>
        </div>
      </div>
    </div>
  );
};

export default StrategicMap;